import axios from "axios";
import { API_BASE } from "../utils/api";
import { getAuthToken } from "../utils/api";

// Set up an Axios interceptor to add the token to all requests
axios.interceptors.request.use((config) => {
  const token = getAuthToken();
  config.headers = config.headers || {};
  config.headers["Content-Type"] = "application/json";
  if (token) {
    config.headers["Authorization"] = `Bearer ${token}`;
  }
  return config;
});

/**
 * Response when message is processed successfully and no review needed
 */
export type FinalResponse = {
  status: "final";
  content: string;
  thread_id: string;
  details: {
    response_type: "final";
    decision?: string;
    reason?: string;
  };
};

/**
 * Response when human approval is needed
 */
export type AwaitingReviewResponse = {
  status: "awaiting_review";
  run_id: string;
  thread_id: string;
  review_prompt: string;
  proposed_args: Record<string, any>;
};

export type AgentResponse = FinalResponse | AwaitingReviewResponse;

export type SendMessageRequest = {
  message: string;
  thread_id?: string;
};

export type MakeDecisionRequest = {
  thread_id: string;
  run_id: string;
  decision: "accept" | "reject" | "edit";
  args?: Record<string, any>;
  reason?: string;
};

export type Thread = {
  thread_id: string;
  doctor: number;
  doctor_name: string;
  title: string;
  status: "active" | "completed" | "archived";
  message_count: number;
  last_message_at: string;
  created_at: string;
  updated_at: string;
};

export type Message = {
  message_id: number;
  thread: string;
  role: "user" | "assistant";
  content: string;
  message_type: "message" | "interrupt" | "decision";
  created_at: string;
};

export type Interaction = {
  interaction_id: string;
  thread: string;
  run_id: string;
  status: "pending" | "accepted" | "rejected" | "edited" | "cancelled";
  review_prompt: string;
  proposed_args: Record<string, any>;
  final_args: Record<string, any> | null;
  decision_reason: string | null;
  created_at: string;
  decided_at: string | null;
};

export type ThreadDetail = Thread & {
  messages: Message[];
  interactions: Interaction[];
  deleted: boolean;
};

const DoctorAgentService = {
  /**
   * Check if doctor-agent service is healthy
   */
  async healthCheck(): Promise<{
    status: string;
    service: string;
    message: string;
    doctor_id: number;
  }> {
    try {
      const response = await axios.get(
        `${API_BASE}/chat/doctor-agent/health_check/`
      );
      return response.data;
    } catch (error: any) {
      console.error("Health check failed:", error);
      throw error.response
        ? error.response.data
        : "Health check failed. Please try again.";
    }
  },

  /**
   * Get all threads for the authenticated doctor
   */
  async getThreads(): Promise<Thread[]> {
    try {
      const response = await axios.get(
        `${API_BASE}/chat/doctor-agent/threads/`
      );
      return response.data;
    } catch (error: any) {
      console.error("Get threads failed:", error);
      throw error.response
        ? error.response.data
        : "Failed to fetch threads. Please try again.";
    }
  },

  /**
   * Get thread details with messages and interactions
   */
  async getThreadDetail(threadId: string): Promise<ThreadDetail> {
    try {
      const response = await axios.get(
        `${API_BASE}/chat/doctor-agent/threads/${threadId}/`
      );
      return response.data;
    } catch (error: any) {
      console.error("Get thread detail failed:", error);
      throw error.response
        ? error.response.data
        : "Failed to fetch thread details. Please try again.";
    }
  },

  /**
   * Delete a thread (soft delete)
   */
  async deleteThread(
    threadId: string
  ): Promise<{ message: string; thread_id: string }> {
    try {
      const response = await axios.delete(
        `${API_BASE}/chat/doctor-agent/threads/${threadId}/`
      );
      return response.data;
    } catch (error: any) {
      console.error("Delete thread failed:", error);
      throw error.response
        ? error.response.data
        : "Failed to delete thread. Please try again.";
    }
  },

  /**
   * Send a message to the doctor agent
   */
  async sendMessage(data: SendMessageRequest): Promise<AgentResponse> {
    try {
      const response = await axios.post(
        `${API_BASE}/chat/doctor-agent/send_message/`,
        data
      );
      return response.data;
    } catch (error: any) {
      console.error("Send message failed:", error);
      throw error.response
        ? error.response.data
        : "Failed to send message. Please try again.";
    }
  },

  /**
   * Make a decision on a pending interaction
   */
  async makeDecision(data: MakeDecisionRequest): Promise<AgentResponse> {
    try {
      const response = await axios.post(
        `${API_BASE}/chat/doctor-agent/make_decision/`,
        data
      );
      return response.data;
    } catch (error: any) {
      console.error("Make decision failed:", error);
      throw error.response
        ? error.response.data
        : "Failed to process decision. Please try again.";
    }
  },

  /**
   * Update thread title
   */
  async updateThreadTitle(threadId: string, title: string): Promise<Thread> {
    try {
      const response = await axios.patch(
        `${API_BASE}/chat/doctor-agent/threads/${threadId}/`,
        { title }
      );
      return response.data;
    } catch (error: any) {
      console.error("Update thread title failed:", error);
      throw error.response
        ? error.response.data
        : "Failed to update conversation title. Please try again.";
    }
  },
};

export default DoctorAgentService;
